import type { LocalizedText } from "@core/api/types";
import { useCharacter } from "@core/character";

export interface SourceInfo {
  book: string;
  /** Null for an entry the user made with own values (source „Eigenbau“). */
  page: number | null;
}

/** The kinds of items that may carry own values. */
export type CustomKind = "item" | "weapon" | "armor" | "shield";

/** Fields of a custom entry in the format of the rule data; prices are amounts as text, e.g. „12 L“. */
export type CustomValues = Record<string, unknown>;

/** A weapon, armour, shield or item with own values that an item names (concept of items, stage 4). */
export interface CustomEntryView {
  id: string;
  kind: CustomKind;
  values: CustomValues;
  /** How many items name the entry; changing its values changes all of them. */
  usedBy: number;
}

type Named = { id: string; name: string };

/** What the form for own values offers; kinds, skills and number of attributes of weapons follow the listed weapons. */
export interface CustomFormView {
  kinds: { id: CustomKind; name: string }[];
  weaponKinds: Named[];
  weaponSkills: Named[];
  weaponAttributes: number;
  features: { id: string; name: string; leveled: boolean; appliesTo: string[] }[];
  availability: Named[];
  complexity: Named[];
}

export interface Allocation {
  skills: Record<string, number>;
  resources: Record<string, number>;
  strengths: { id: string; param?: string | null }[];
  masteries: { id: string; param?: string | null; skill?: string | null }[];
  languages: string[];
}

/** One slot of a chosen module: printed, decided or still open, with the ids of the open choices that belong to it. */
export interface SlotView {
  id: string;
  category: "staerke" | "ressource" | "fertigkeit" | "meisterschaft" | "sprache";
  kind: "fixed" | "pick" | "pool";
  status: "fixed" | "chosen" | "open";
  granted: string[];
  options: string[];
  /** What all options share, e.g. the skill of the masteries; the options then name only what differs. */
  optionGroup?: string | null;
  values: number[];
  note?: string | null;
  choiceId?: string | null;
  paramChoiceId?: string | null;
  /** Key of the chosen option whose free-text parameter can be changed alone. */
  paramKey?: string | null;
}

export interface StepView {
  id: string | null;
  name: string | null;
  useModule: boolean;
  individual: boolean;
  variant: { id: string; name: string } | null;
  summary: string | null;
  /** Description from a local text package; absent without one. */
  description?: LocalizedText | null;
  source: SourceInfo | null;
  complete: boolean;
  allocation: Allocation;
  /** What the module grants as printed, for comparison while individualising; null for a freely created step, even with the id of a module chosen before. */
  printed: Allocation | null;
  choices: Record<string, { key: string; value: number; param?: string | null }[]>;
  /** The module's slots; null for a freely created step. */
  slots: SlotView[] | null;
}

/** Value keys of one budget: points left, in total and spent. */
export interface BudgetKeys {
  left: string;
  total: string;
  spent: string;
}

/** Budgets of start experience (`startXp`), of step 7 (`frei`) and of module steps distributed freely or individualised. */
export type AllocationBudgets = Record<string, Partial<Record<"skills" | "resources" | "strengths" | "masteries" | "xp", BudgetKeys>>>;

export interface StepLimits {
  skillPoints: number;
  perSkillMax: number;
  magicSchoolMaxPoints: number;
  magicSchoolMaxCount?: number | null;
  resourcePoints: number;
  strengthPoints: number;
  masteries: number;
  masteryLevel: number;
  /** A free distribution of the step may set a core resource below 0 (GRW S. 24, SM-ABST-01). */
  negativeCoreResources?: boolean;
}

export interface CatalogEntry {
  id: string;
  name: string;
  summary?: string | null;
  description?: LocalizedText | null;
  source: SourceInfo;
}

export interface RaceEntry extends CatalogEntry {
  sizeClass: number;
  fixed: Record<string, number>;
  free: number;
  strengths: string[];
}

export interface MapAreaEntry {
  map: string;
  /** Polygons of points `[x, y]` in percent of the map image. */
  polygons: number[][][];
  note?: string | null;
}

export interface MapEntry {
  id: string;
  name: string;
  width: number;
  height: number;
}

export interface RegionEntry extends CatalogEntry {
  /** The top-level region this one lies in; absent for a top-level region or when the parent is not in the active ruleset. */
  parent?: string | null;
  mapAreas: MapAreaEntry[];
}

export interface CultureEntry extends CatalogEntry {
  /** Ids of regions from `catalog.regions`, possibly several (E39), top-level and sub-regions; empty when the data name none (SM-G-086). */
  regions: string[];
  mapAreas: MapAreaEntry[];
  typicalRaces: string[];
  typicalAncestries: string[];
  typicalEducations: string[];
}

export interface EducationEntry extends CatalogEntry {
  variants: (CatalogEntry & { cultures: string[]; races: string[]; requirement?: string | null })[];
}

export interface MoonSignEntry extends CatalogEntry {
  gifts: { heroLevel: number; summary: string }[];
}

/** The chosen moon sign; a gift is unlocked once the hero level reaches it (GRW p. 81–83). */
export interface MoonSignView extends CatalogEntry {
  gifts: { heroLevel: number; summary: string; unlocked: boolean }[];
}

export interface ResourceEntry extends CatalogEntry {
  core: boolean;
  min: number;
  max: number;
  multipleInstances: boolean;
  /** What every instance names, e.g. the organisation of a Rang; its value names the instance (GRW p. 79). */
  parameter?: string | null;
}

export interface SkillView {
  id: string;
  name: string;
  category: "kampf" | "allgemein" | "magie";
  attributes: string[] | null;
  value: string;
  /** The skill value during play, e.g. with the wound penalty. */
  play: string;
  points: string;
  max: number;
  /** Value key of the EP for the next point; career mode only. */
  cost?: string | null;
  description?: LocalizedText | null;
  masteries: {
    id: string;
    name: string;
    level: number;
    param?: string | null;
    origin: string;
    free: boolean;
    /** Missing prerequisite; the mastery is held but cannot be used yet. */
    missing?: string | null;
    summary?: string | null;
    description?: LocalizedText | null;
    source: SourceInfo;
  }[];
}

/** Struck-off life and focus points; counts per loss kind id, the rest are value keys. */
export interface LossesView {
  kinds: { id: string; name: string; lifeName: string; symbol: string; breather: boolean; endsAs?: string | null; summary?: string | null; source: SourceInfo }[];
  life: { boxes: string; available: string; perRow: string; counts: Record<string, number>; level: string; penalty: string; levels: { level: number; name: string; /** Value key of the penalty after reductions, at most 0. */ penalty: string }[] };
  /** What a rest restores of one kind on one track beyond what a breather restores; value is the key of the amount. */
  rest: { id: string; name: string; track: "lp" | "fo"; kind: string; value: string; summary?: string | null; source: SourceInfo }[];
  /** What changes the values during play, e.g. the wound penalty. */
  playEffects: { id: string; name: string; summary?: string | null; source: SourceInfo }[];
  focus: { boxes: string; available: string; counts: Record<string, number> };
}

export interface WeaponView {
  instance: string;
  id: string;
  name: string;
  quality: number;
  /** The weapon shoots ammunition; ammo is the assigned stock with what is left. */
  usesAmmo: boolean;
  ammo: { instance: string; name: string; quantity: number } | null;
  /** The containers on the body the weapon lies in, e.g. „Rucksack“; null when it lies directly on the body. */
  container: string | null;
  profiles: { kind: string; skill: string; attributes: string; fw: string; fwPlay: string; damage: string; wgs: string; range?: number | null; rangeKey?: string | null; features: FeatureView[] }[];
}

/** What a mastery changes at cost or range of a spell, shown next to the printed value (E4, SM-F-078). */
export interface SpellNote {
  text: string;
  source: SourceInfo;
}

/** A shield with active defense and bash (GRW p. 161, 163); keys are null when the rules form no such value. */
export interface ShieldView {
  instance: string;
  id: string;
  name: string;
  equipped: boolean;
  defenseSkill?: string | null;
  defenseAttributes?: string | null;
  defense: string | null;
  defensePlay: string | null;
  bashSkill?: string | null;
  bashAttributes?: string | null;
  bash: string | null;
  bashPlay: string | null;
  bashDamage: string | null;
  bashWgs: string | null;
  features: FeatureView[];
}

/** A feature of a weapon, armor or shield; the name carries the level. */
export interface FeatureView {
  id: string;
  name: string;
  description?: LocalizedText | null;
  source: SourceInfo | null;
}

export type ItemKind = "weapon" | "armor" | "shield" | "item" | "custom" | "ammo";
export type ItemOrigin = "purchased" | "crafted" | "basicKit" | "tools" | "startWeapon" | "received";

export interface PlacementNodeView {
  id: string;
  parent: string | null;
  role: "body" | "storage" | "place" | "carrier" | "container" | "item";
  label: string;
  canHold: boolean;
  canWear: boolean;
  loadKey: string | null;
  capacityKey: string | null;
  carriedKey: string | null;
  /** Pieces of the line; null for a root. */
  quantity: number | null;
}

/**
 * A place besides body and storage (O5): free with its own name, or tied to an instance of Zuflucht, whose name and level it shows.
 * held: the character still holds the instance it is tied to; a tied place without it stays, without the tie.
 */
export interface PlaceView {
  id: string;
  label: string;
  name: string | null;
  ref: string | null;
  held: boolean;
  /** Value key of the level of the instance; null without a held instance. */
  levelKey: string | null;
  note: string | null;
  /** Things at the place, carriers and their contents included; removing the place puts all of them into storage. */
  count: number;
}

/** A choice of a legendary power: its options as value and label; a strength names its level and own parameter. */
export interface LegendaryPowerParam {
  label: string;
  options: { value: string; label: string; level?: number | null; param?: { label: string; options: string[] | null } | null }[] | null;
  open: boolean;
}

export interface LegendaryPowerDef {
  id: string;
  name: string;
  /** Relic points; null when they follow the level of the chosen strength. */
  cost: number | null;
  costByStrength: boolean;
  repeatable: number;
  appliesTo: string[];
  exclusiveGroup: string | null;
  usage: string | null;
  wielded: boolean;
  parameter: LegendaryPowerParam | null;
  detail: LegendaryPowerParam | null;
  grantsStrength: boolean;
  summary: string | null;
  description?: LocalizedText | null;
  source: SourceInfo;
}

export interface LegendaryPowerRow {
  index: number;
  id: string;
  name: string;
  param: string | null;
  paramName: string | null;
  detail: string | null;
  cost: number;
  /** The material gives the power without cost, e.g. Mondstahl (MSK p. 98). */
  free: boolean;
  values: { key: string; label: string; unit: string | null }[];
}

/** A choice at a piece: an improvement or personalization with what is chosen for it. */
export interface ItemChoiceRow {
  index: number;
  id: string;
  name: string;
  qualityCost?: number;
  param: string | null;
  paramName: string | null;
  detail: string | null;
  detailName: string | null;
}

/** The maker of a piece: the character himself with his crafting skill, or another with grade and masteries (SM-G-131). */
export interface Crafter {
  self: boolean;
  skill?: string | null;
  focus?: string | null;
  grade?: number | null;
  masteries: string[];
  ownMaterial: boolean;
}

export interface InventoryView {
  instance: string;
  kind: ItemKind;
  ref: string | null;
  name: string;
  customName: string | null;
  quantity: number;
  quality: number;
  /** Improvements, one row per application with its choices (MSK p. 90–93; SM-G-123). */
  improvements: ItemChoiceRow[];
  /** Improvements a material gives without quality, as labels (SM-G-137). */
  grantedImprovements: string[];
  /** What the piece counts as for the appliesTo of improvements, e.g. waffe, nahkampfwaffe, kleidung. */
  kinds: string[];
  /** Entry of the materials the piece is made of, with its material quality and what it does (MSK p. 94–98). */
  material: { id: string; name: string; quality?: number | null; summary?: string | null } | null;
  /** Surcharge per piece actually paid for the material, formatted; null when the proposed one applies. */
  materialPrice: string | null;
  /** Value key of the surcharge the rule data propose for the material of one piece. */
  materialSurchargeKey: string;
  /** Quality of the piece: the chosen one, at least the quality of its material (MSK p. 94). */
  effectiveQuality: number;
  /** Quality whose levels pay improvements and powers; for a relic the chosen one, without the least quality of its material (SM-G-103). */
  slotQuality: number;
  /** Legendary powers of a relic, one row per choice (MSK p. 102). */
  legendaryPowers: LegendaryPowerRow[];
  /** Value key of the relic points the legendary powers cost. */
  powerCostKey: string;
  /** A relic point pays the property of the material (MSK p. 100). */
  materialFromRelic: boolean;
  /** The material quality leaves no relic point for its property, e.g. Mondstahl (SM-G-103). */
  materialPointBlocked: boolean;
  /** The choice a material asks for, e.g. the magic school of Mondglas, with its name. */
  materialParam: string | null;
  materialParamName: string | null;
  /** Personalizations of the piece, see SmView.personalizations (MSK p. 99). */
  personalizations: ItemChoiceRow[];
  /** The owner personalized the piece himself and paid only the material (SM-G-113). */
  personalizedBySelf: boolean;
  /** The blessing of a relic: more quality levels for improvements, as many as SmView.blessing names (MSK p. 89; SM-G-130). */
  blessed: boolean;
  /** The piece is a full set of clothing or armour, for materials that act only so (SM-G-134). */
  fullSet: boolean;
  /** The makes of its entry the piece has, and those it may have with their surcharge per piece (MSK p. 52; SM-G-158). */
  variants: string[];
  variantOptions: { id: string; name: string; price: string; availability: string | null; granted: string[] }[] | null;
  /** Who made the piece (SM-G-131); null when unknown. */
  crafter: Crafter | null;
  /** Value key of the highest quality, when there is such a value. */
  maxQualityKey: string | null;
  /** Value key of the quality levels improvements may spend; the value exists once the piece has quality, quality from its material or a blessing (RA-AUSR-2). */
  qualityBudgetKey: string;
  /** Value key of the levels of negative quality; the value exists only while the quality, after the material, is negative and the improvements act reversed (RA-AUSG-11). */
  negativeQualityKey: string;
  /** Value keys of making the piece oneself: difficulty, progress points, hours and cost of the material (MSK p. 84–87). */
  craftKeys: { schwierigkeit?: string; fortschritt?: string; stunden?: string; material: string | null } | null;
  equipped: boolean;
  parryWeapon: boolean;
  /** Where the item lies: body, storage or the instance of a container or carrier; null for a carrier, a location of its own. */
  parent: string | null;
  /** The location with its root, e.g. „Körper › Rucksack“. */
  location: string;
  /** Lies under the body, also inside a container there; derived from the location, never stored. */
  carried: boolean;
  role: PlacementNodeView["role"];
  /** Lies directly on the body, so it may be worn. */
  canWear: boolean;
  /** Can be worn at all: armor, shields and items of a category the data mark as worn, e.g. clothing. */
  wearable: boolean;
  /** A pack animal or vehicle that still lies somewhere as an item (bought before locations existed). */
  needsSetup: boolean;
  /** Inputs of a pack animal or vehicle: strength and capacity. */
  carrier: { strength?: number | null; capacity?: number | null } | null;
  /** A pack animal or vehicle by the list; one set up by the user (canCarry before) can be taken back. */
  carrierByList: boolean;
  /** An own item that can be set up as a pack animal or vehicle (user decision of 3.11.2026). */
  canCarry: boolean;
  /** The instance of Kreatur the animal comes from instead of being bought, and as shown (GRW p. 78, SM-G-192). */
  creature: string | null;
  creatureLabel: string | null;
  artifact: ArtifactState | null;
  origin: ItemOrigin;
  originLabel: string;
  /** The instance of the resource Relikt the item embodies (`relikt` or `relikt#Name`), in any mode (GRW p. 79); see SmView.resources. */
  relic: string | null;
  /** The instance as shown, e.g. „Relikt (Erbstück)“. */
  relicLabel: string | null;
  /** For a weapon: instance of the assigned ammunition stock. */
  ammo: string | null;
  /** For a stock of ammunition: its kind of special projectile (MSK p. 35). */
  projectile?: string | null;
  usesAmmo: boolean;
  /** Price of one piece in Telare: the reference price, an own price or for a relic the list price with what is bought on top. */
  price: number;
  priceText: string;
  listPrice: number | null;
  /** Reference price: list price with the surcharges of quality and artifact quality, formatted. */
  listPriceText: string;
  /** The own price, formatted; null when the list price applies. */
  customPrice: string | null;
  /** Value keys of the price of one piece and of the whole line, each with its derivation. */
  priceKey: string;
  totalKey: string;
  /** Value key of the reference price, the basis for selling (GRW p. 110). */
  referenceKey: string;
  /** Value key of what a relic costs beyond its relic points (GRW p. 143, SM-G-070); the value exists only while the item is a relic. */
  relicPurchaseKey: string;
  /** Value key of the relic points the item takes from its instance; null when it is no relic. */
  relicPointsKey: string | null;
  /** The levels of quality and artifact quality the relic points pay, when split by the user (E24); null for the automatic split. */
  relicSplit: RelicSplit | null;
  /** Value key of the proposed proceeds of selling one piece. */
  saleKey: string | null;
  /** Value keys of hardness and durability, absent for pieces without hardness (GRW p. 179). */
  hardnessKey: string | null;
  durabilityKey: string | null;
  listHardness: number | null;
  /** Own hardness; null when the listed one applies (SM-G-071). */
  customHardness: number | null;
  /** Skill and focus a skill bonus of a piece other than a weapon helps with (GRW p. 142). */
  focus: { skill: string; skillName: string; name: string } | null;
  /** Left out of the own carried load (GRW p. 181, E44): a flag per item, set when the equipment of creation is taken over, then by hand. */
  baseEquipment: boolean;
  /** Value key of the availability with quality (GRW p. 181); null without a listed availability. */
  availabilityKey: string | null;
  load: number | null;
  customLoad: number | null;
  /** Value key of the quality levels the improvements cost. */
  qualityKey: string;
  /** Value keys of the load of one piece and of the whole line. */
  loadKey: string;
  loadTotalKey: string;
  note: string | null;
  /** Features of armor and shields; those of weapons stand with the weapon profiles. */
  features: FeatureView[];
  /** The entry with own values the item names; null for an entry of the rule data. */
  custom: CustomEntryView | null;
  artifactProperties: {
    id: string;
    name: string;
    level: number;
    /** The anchored spell, for a property with spellGrade. */
    param?: string | null;
    paramName?: string | null;
    maxLevel?: number | null;
    /** The property anchors a spell of this grade (GRW p. 254). */
    spellGrade?: number | null;
    description?: LocalizedText | null;
    source: SourceInfo | null;
  }[];
  description?: LocalizedText | null;
  source: SourceInfo | null;
}

/** A simple artifact as stored: its kind (structure giver when absent), artifact quality and properties (GRW p. 254). */
export interface ArtifactState {
  kind?: string | null;
  quality: number;
  properties: { id: string; level: number; param?: string | null }[];
  note?: string | null;
}

export interface RelicSplit {
  quality: number;
  artifactQuality: number;
}

/** Dice for one description field from the tables of the race; formula is how the book writes it, e.g. "160 + 5W10". */
export interface AppearanceRoll {
  dice: string;
  count: number;
  sides: number;
  formula: string;
}

/** A deity of the lists GRW p. 312–313 and WELT p. 263–266; the schools are recommendations for priests. */
export interface DeityView {
  id: string;
  name: string;
  aliases: string[];
  gender: string | null;
  aspects: string[];
  region: string;
  /** Recommended and unusual schools as the list prints them, e.g. „+ Lichtmagie; – Schattenmagie“; null without any. */
  schools: string | null;
  summary: string | null;
  source: SourceInfo;
}

export interface SmView {
  race: { id: string; name: string; sizeClass: number; description?: LocalizedText | null; source: SourceInfo } | null;
  culture: StepView | null;
  ancestry: StepView | null;
  education: StepView | null;
  attributes: { id: string; name: string; short: string; value: string; start: string; points: number; cost?: string | null; description?: LocalizedText | null }[];
  /** play is the value during play, e.g. with the wound penalty; absent where there is none. */
  derived: { id: string; name: string; short: string; value: string; play?: string | null; formula: string; description?: LocalizedText | null }[];
  skills: SkillView[];
  /** Skill groups with the category all their members share. */
  skillGroups: { id: string; name: string; category: SkillView["category"] | null; description?: LocalizedText | null; source: SourceInfo }[];
  strengths: { id: string; name: string; param?: string | null; count: number; level: number; origins: string[]; canAddMore: boolean; summary?: string | null; description?: LocalizedText | null; source: SourceInfo }[];
  /**
   * relicPoints: for an instance of Relikt, the value keys of its relic points, which pay for the quality of the relics bound to it;
   * relicSurcharge: the points the game master charges on top for that instance, as entered (GRW p. 79, SM-STAE-11).
   */
  resources: {
    key: string;
    id: string;
    name: string;
    param?: string | null;
    value: string;
    cost?: string | null;
    /** Career mode: points a loss may take, down to the minimum of a core resource or to 0 for an optional one (SM-RA-020). */
    lossMax?: number | null;
    relicPoints?: BudgetKeys | null;
    relicSurcharge?: number | null;
    /** Ausgestaltung: short note and long description entered by the user. */
    note?: string | null;
    details?: string | null;
    /** The instance can be set up as a place (Zuflucht, held with points); place is the id of the place set up for this one. */
    placeable?: boolean;
    place?: string | null;
    /** A held instance of a resource whose instances are creatures (Kreatur), so an animal may come from it; animal is the item that does. */
    creature?: boolean;
    animal?: string | null;
    description?: LocalizedText | null;
    source?: SourceInfo | null;
    /** A hint for raising the resource in career mode, e.g. how a relic grows (MSK p. 101; SM-G-159). */
    advanceNote?: { text: string; source: SourceInfo } | null;
  }[];
  languages: { id: string; name: string; origin: string; description?: LocalizedText | null }[];
  spells: { id: string; name: string; school: string; schoolName: string; grade: number | null; origin: string; free: boolean; difficulty: string; cost: string; costNotes?: SpellNote[]; castingTime: string; range: string; rangeNotes?: SpellNote[]; duration: string; area?: string | null; summary?: string | null; description?: LocalizedText | null; source: SourceInfo }[];
  moonSign: MoonSignView | null;
  weaknesses: { id?: string | null; name: string; note?: string | null; details?: string | null; description?: LocalizedText | null }[];
  weapons: WeaponView[];
  shields: ShieldView[];
  losses: LossesView;
  inventory: InventoryView[];
  /** Value keys of the value and load of the whole inventory. */
  inventoryTotals: { value: string; load: string };
  /** Improvements whose bonus goes to a focus the game master sets per piece. */
  focusImprovements: string[];
  /** The kinds of simple artifacts, structure giver and scroll (GRW p. 254). */
  artifactKinds: { id: string; name: string }[];
  /** How many effects a scroll carries, with the page of its prices. */
  scrolls: { maxEffects: number; source: SourceInfo | null } | null;
  /** The location tree: body, storage, carriers and containers with the value keys of their loads (E44). */
  placement: { nodes: PlacementNodeView[] };
  places: PlaceView[];
  /** Value keys of the optional rule on carried load; null while it is off. */
  carrying: { capacity: string; carried: string; excess: string; source: SourceInfo } | null;
  startKit: {
    items: string[];
    choices: { id: string; label: string; note?: string | null; options: string[] }[];
    added: boolean;
    toolsMax: number;
    toolsMaxText: string;
    weaponMax: number;
    weaponMaxText: string;
    origins: { id: ItemOrigin; label: string }[];
    careerOrigins: { id: ItemOrigin; label: string }[];
    /** Material share of the price when crafting, e.g. "1/3" (GRW p. 143). */
    materialShare: string | null;
    craftingSource: SourceInfo | null;
    /** Proposed proceeds of a sale as share of the reference price, e.g. „1/2“ (GRW p. 110). */
    saleShare: string | null;
    saleSource: SourceInfo | null;
    /** How far an own hardness may differ from the listed one without a warning (GRW p. 179, SM-G-071). */
    hardnessVariation: number | null;
    negativeQualityPercent: number | null;
    /** Change of merchant prices in percent from masteries, e.g. Feilscher; booked when a purchase names none. */
    purchaseChangeKey: string;
    /** Checks of crafting, forging, repairing and appraising from the rule data, shown as hints; the app does not roll them. */
    craftingHints: { label: string; text: string; source: SourceInfo }[];
    durabilitySource: SourceInfo;
    source: SourceInfo;
  };
  /** Legendary powers of relics from the loaded packages (MSK p. 102–103); empty without Mondstahlklingen. */
  legendaryPowers: LegendaryPowerDef[];
  /** Whether the rule options allow legendary powers now (SM-G-104). */
  legendaryPowersAllowed: boolean;
  /** Highest material quality whose property a relic point can still pay; null without the resource Relikt (SM-G-103). */
  relicMaterialMaxQuality: number | null;
  /** The spell the blessing of a piece means, with the quality levels its data give (MSK p. 89; RA-BKONST-1); null when no or several spells carry them. */
  blessing: { name: string; itemQualityBonus: number; source: SourceInfo } | null;
  /** Personalizations from the loaded packages (MSK p. 99); empty when no package names any. */
  personalizations: {
    id: string;
    name: string;
    appliesTo: string[];
    parameter: LegendaryPowerParam | null;
    detail: LegendaryPowerParam | null;
    /** Whether the owner can do the work himself for less (SM-G-113). */
    ownWork: boolean;
    summary?: string | null;
    source: SourceInfo;
  }[];
  /** Whether the rule option Personalisierung is on (SM-G-115). */
  personalizationAllowed: boolean;
  /** The choices improvements ask for, by entry. */
  improvementChoices: { id: string; parameter: LegendaryPowerParam | null; detail: LegendaryPowerParam | null; appliesTo: string[] }[];
  /** The choice materials ask for, by entry. */
  materialChoices: { id: string; parameter: LegendaryPowerParam | null }[];
  /** What the maker of a piece may name: crafting skills, masteries beyond the grade, and the workshops of the character (MSK p. 87, 99). */
  crafting: {
    skills: { id: string; name: string }[];
    masteries: { id: string; name: string }[];
    workshopResource: string | null;
    workshops: Record<string, { skill: string; name: string }>;
  };
  /** Weapons that shoot ammunition, with the price of one piece from their table (SM-G-013). */
  ammunition: { ref: string; name: string; price: number | null; priceText: string | null; kind?: string | null; source: SourceInfo }[];
  /** Kinds of special projectiles, only with their optional rule (MSK p. 35–37). */
  projectiles: ProjectileView[] | null;
  /** Buying and selling by market (optional rule, MSK p. 104–105); null when it is off. */
  trading: {
    markets: { id: string; name: string }[];
    quickSale: { id: string; name: string; percent: number }[];
    /** How much the list price of purchases rises, in percent, and for how many months after a fumbled search for a merchant (MSK p. 104); null without it. */
    fumbleSurcharge: { percent: number; months: number } | null;
    source: SourceInfo | null;
  } | null;
  /** Haggling by the rules (GRW p. 111), the source of the rule; null without it. */
  haggling: SourceInfo | null;
  /** A held mastery for haggling on the black market, e.g. Schwarzmarkt (GRW p. 132; SM-RA-060); null without one. */
  blackMarketMastery: string | null;
  /** Shot projectiles may be gathered (optional rule, MSK p. 36), with the die of the hits. */
  recoveryAllowed: boolean;
  recoveryDie: number | null;
  itemCategories: { id: string; name: string }[];
  customForm: CustomFormView;
  startXp: { index: number; kind: string; label: string; cost: number }[];
  /** Step 9 was closed; it counts as done after a purchase or this (GRW p. 25). */
  startXpConfirmed: boolean;
  finishing: {
    masteries: { index: number; id: string; name: string; param?: string | null; skill?: string | null }[];
    spells: { index: number; id: string; name: string; school: string; schoolName: string }[];
    /** The user closed the step; without open choices it counts as done only then. */
    confirmed: boolean;
  };
  advancements: { id: string; kind: string; target?: string | null; from: number; to: number; cost: number; at: string }[];
  allocationBudgets: AllocationBudgets;
  budgets: Record<"attributes" | "strengths" | "resources" | "skills" | "startXp" | "money" | "startMoney" | "moneySpent" | "tools" | "startWeapon", string> & {
    /** Career mode: start experience open again after a purchase of creation was taken back in the log; null without a switch to career mode. */
    startXpReopened?: string | null;
  };
  /** Start-experience costs during creation and in career mode while start experience can be open again; otherwise null. */
  costs: Record<string, unknown> | null;
  /** Highest spell grade and mastery threshold that start experience buys (GRW p. 25); null when `costs` is. */
  startXpLimits: { spellGrade: number; masteryLevel: number } | null;
  /** Value keys of the EP for a mastery per threshold; career mode only. */
  masteryCosts: string[] | null;
  /** Career mode: per skill with points for a mastery, its masteries, each with the reason it cannot be bought yet. */
  masteryOffers: MasteryOffer[];
  /** The same for a purchase with start experience (up to its threshold, in skills with the points of its table); empty when it cannot be spent. */
  startXpMasteryOffers: MasteryOffer[];
  /** Creation: per module step whose free distribution includes masteries, every skill with the masteries of the step's threshold. */
  moduleMasteryOffers: Record<string, MasteryOffer[]>;
  concept: string;
  description: Record<string, string | number | null>;
  /** The listed deity the field Gottheit names by name or further name; null for an own deity or none (SM-RA-130). */
  deity: DeityView | null;
  /** What the race lets roll for the description, by description field, and its weight range (GRW p. 26, 32–37). */
  appearance: { rolls: Partial<Record<"hair" | "eyes" | "skin" | "height", AppearanceRoll>>; weight: { min: number; max: number } | null; source: SourceInfo } | null;
  notes: string;
  freePoints: Allocation;
  heroLevel: number;
  catalog?: {
    races: RaceEntry[];
    /** Regions the culture step groups by, in data order; sub-regions name their top-level region as `parent`. */
    regions: RegionEntry[];
    cultures: CultureEntry[];
    /** Maps whose images an admin uploads; the cultures' `mapAreas` refer to them. */
    maps: MapEntry[];
    ancestries: CatalogEntry[];
    educations: EducationEntry[];
    moonSigns: MoonSignEntry[];
    resources: ResourceEntry[];
    attributeRules: { points: number; min: number; max: number };
    freePoints: { strengthPoints: number; resourcePoints: number; skillPoints: number };
    limits: { skillMax: number; resourceMax: number; resourceApprovalAbove: number };
    steps: Record<string, StepLimits>;
    startXp: number;
  };
}

export interface ProjectileView {
  id: string;
  name: string;
  standard: boolean;
  ammunition: string[];
  price: string | null;
  /** Availability of the kind (MSK p. 35–37). */
  availability: string | null;
  /** Skill and focus of making the kind. */
  crafting: string | null;
  summary?: string | null;
  description?: LocalizedText | null;
  source: SourceInfo;
}

export function useSm() {
  const context = useCharacter();
  const view = context.character.evaluation.view as unknown as SmView;
  /** Money in the units of the character's rule set. */
  const money = (telare: number | null | undefined) => (telare == null ? "" : context.amount("geld", telare));
  /** The number behind a budget key, 0 when it is unknown. */
  const budget = (key: string) => context.value(key)?.number ?? 0;
  return { ...context, view, money, budget };
}

/** Catalog filter: strengths the character cannot take again and languages it already speaks. */
/** What a skill offers for a new mastery: each mastery with the reason the engine gives why it cannot be taken, or null. */
export interface MasteryOffer {
  skill: string;
  masteries: { id: string; blocked: string | null }[];
}

export function alreadyTaken(view: SmView, catalog: string) {
  return (item: { id: string }): string | null => {
    if (catalog === "strengths") {
      const owned = view.strengths.filter((s) => s.id === item.id);
      return owned.length > 0 && !owned.some((s) => s.canAddMore) ? `bereits vorhanden (${[...new Set(owned.flatMap((s) => s.origins))].join(", ")})` : null;
    }
    if (catalog === "languages") {
      const known = view.languages.find((l) => l.id === item.id);
      return known ? `beherrscht (${known.origin})` : null;
    }
    return null;
  };
}

export function page(source: SourceInfo | null | undefined): string {
  if (!source) return "";
  return source.page == null ? source.book : `${source.book} S. ${source.page}`;
}

/** Label of a resource instance key (`id` or `id#name`). */
export function resourceLabel(view: SmView, key: string): string {
  const hash = key.indexOf("#");
  const id = hash < 0 ? key : key.slice(0, hash);
  const resource = view.resources.find((r) => r.id === id)?.name ?? view.catalog?.resources.find((r) => r.id === id)?.name ?? id;
  return hash < 0 ? resource : `${resource} (${key.slice(hash + 1)})`;
}

/** Catalog details of a resource or strength as the backend serialises the rule entry. */
export interface EntryDetails {
  multipleInstances?: boolean;
  parameter?: { label: string; options?: string[] | null; optionsFrom?: string | null; value?: string | null } | null;
}

export const kindLabels: Record<ItemKind, string> = { weapon: "Waffe", armor: "Rüstung", shield: "Schild", item: "Gegenstand", custom: "Eigener Gegenstand", ammo: "Munition" };

export const skillCategories = [
  { id: "allgemein", label: "Allgemein" },
  { id: "kampf", label: "Kampf" },
  { id: "magie", label: "Magie" },
] as const;

export function signed(value: number): string {
  return value > 0 ? `+${value}` : String(value);
}

export function joinOr(items: string[]): string {
  if (items.length <= 1) return items[0] ?? "";
  return `${items.slice(0, -1).join(", ")} oder ${items[items.length - 1]}`;
}
